import { useStore, type AuthUser } from '../state/store';

/**
 * SPblock cloud API client (Cloudflare Pages Functions).
 * Bearer-token auth; 401 clears the stored session.
 */

export interface CloudProjectMeta {
  id: string;
  name: string;
  sizeBytes: number;
  updatedAt: string;
}

export interface CloudProjectList {
  projects: CloudProjectMeta[];
  usedBytes: number;
  quotaBytes: number;
}

export interface CloudProjectFull {
  id: string;
  name: string;
  data: string;
  sizeBytes: number;
  updatedAt: string;
}

export interface AdminUserRow {
  id: string;
  name: string;
  role: 'main_admin' | 'org_admin' | 'org_manager' | 'member';
  orgId: string | null;
  orgName: string | null;
  disabled: boolean;
  createdAt: string;
  projectCount: number;
  usedBytes: number;
}

export interface AdminOrgRow {
  id: string;
  name: string;
  quotaMb: number;
  maxUsers: number;
  disabled: boolean;
  createdAt: string;
  userCount: number;
  usedBytes: number;
}

export interface OrgMemberRow {
  id: string;
  username: string;
  role: 'org_admin' | 'org_manager' | 'member';
  disabled: boolean;
  createdAt: string;
  projectCount: number;
  usedBytes: number;
}

export interface OrgInfo {
  name: string;
  quotaMb: number;
  maxUsers: number;
  disabled: boolean;
}

export class CloudError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

function token(): string | null {
  return useStore.getState().auth.token;
}

export async function api<T>(
  path: string,
  init?: Omit<RequestInit, 'body'> & { body?: unknown; retried?: boolean },
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const usedToken = token();
  if (usedToken) headers['Authorization'] = `Bearer ${usedToken}`;

  let res: Response;
  try {
    res = await fetch(path, {
      method: init?.method ?? (init?.body !== undefined ? 'POST' : 'GET'),
      headers,
      body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
  } catch {
    throw new CloudError('The SPblock cloud is unreachable. Check your connection or deploy state.', 0);
  }

  if (res.status === 401) {
    // Single-active-session: a 401 means THIS token lost a login race. Only
    // clear the session when the failed token is still the current one; if a
    // newer session exists (another tab signed in), prioritize it and retry.
    const current = useStore.getState().auth.token;
    if (current && current !== usedToken && !init?.retried) {
      return api<T>(path, { ...init, retried: true });
    }
    useStore.getState().setAuth(null, null);
    throw new CloudError(
      current === usedToken
        ? 'Your session expired — sign in again.'
        : 'Signed out. Sign in again to continue.',
      401,
    );
  }

  const data = (await res.json().catch(() => null)) as (T & { error?: string }) | null;
  if (!res.ok) {
    throw new CloudError(data?.error ?? `Request failed (HTTP ${res.status}).`, res.status);
  }
  return data as T;
}

/* ---------------- auth ---------------- */

/** Main-admin sign in. On an empty deployment this bootstraps the admin. */
export function login(email: string, password: string): Promise<{ token: string; user: AuthUser }> {
  return api('/api/auth/login', { body: { email, password } });
}

/** Organization account sign in (organization name + username + password). */
/** Self-serve registration (only when the admin enabled it). */
export function register(email: string, password: string): Promise<{ token: string; user: AuthUser }> {
  return api('/api/auth/register', { body: { email, password } });
}

/** Public: is self-serve signup currently enabled? */
export function authConfig(): Promise<{ signupEnabled: boolean }> {
  return api('/api/auth/register');
}

/** Public: active organization names for the sign-in picker. */
export function listOrgs(): Promise<{ orgs: string[] }> {
  return api('/api/orgs');
}

export type OrgLoginResult =
  | { token: string; user: AuthUser }
  | { mustSetupPassword: true; setupToken: string; user: { username: string; orgName: string } };

export async function orgLogin(
  orgName: string,
  username: string,
  password: string,
): Promise<OrgLoginResult> {
  return api('/api/auth/org-login', { body: { orgName, username, password } });
}

/**
 * First sign-in of a passwordless member: choose a password using the
 * short-lived setup token, then receive a real session.
 */
export async function setupPassword(
  setupToken: string,
  password: string,
): Promise<{ token: string; user: AuthUser }> {
  let res: Response;
  try {
    res = await fetch('/api/auth/setup-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${setupToken}` },
      body: JSON.stringify({ password }),
    });
  } catch {
    throw new CloudError('The SPblock cloud is unreachable. Check your connection or deploy state.', 0);
  }
  const data = (await res.json().catch(() => null)) as
    | (({ token: string; user: AuthUser }) & { error?: string })
    | null;
  if (!res.ok) throw new CloudError(data?.error ?? `Request failed (HTTP ${res.status}).`, res.status);
  return data as { token: string; user: AuthUser };
}

/** Kills the active session server-side (single-session teardown). */
export async function logout(): Promise<void> {
  try {
    await api('/api/auth/logout', { body: {} });
  } catch {
    /* the session is cleared locally regardless */
  }
}

/* ---------------- projects ---------------- */

export function listProjects(): Promise<CloudProjectList> {
  return api('/api/projects');
}

export function saveProject(name: string, data: string): Promise<{ project: CloudProjectMeta }> {
  return api('/api/projects', { body: { name, data } });
}

export function openProject(id: string): Promise<{ project: CloudProjectFull }> {
  return api(`/api/projects/${encodeURIComponent(id)}`);
}

export function deleteProject(id: string): Promise<{ ok: boolean }> {
  return api(`/api/projects/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Enable sharing (idempotent) and get the project's share code. */
export function shareProject(id: string): Promise<{ shareCode: string }> {
  return api(`/api/projects/${encodeURIComponent(id)}/share`, { body: {} });
}

/** Open a project someone shared by its code (a snapshot you can save as your own). */
export function openShared(code: string): Promise<{ project: { name: string; data: string } }> {
  return api(`/api/shared/${encodeURIComponent(code.trim().toLowerCase())}`);
}

/** Change the signed-in account's password. */
export function changePassword(currentPassword: string, newPassword: string): Promise<{ ok: boolean }> {
  return api('/api/auth/change-password', { body: { currentPassword, newPassword } });
}

/* ---------------- admin: accounts ---------------- */

export function adminListUsers(): Promise<{ users: AdminUserRow[] }> {
  return api('/api/admin/users');
}

export function adminUpdateUser(
  id: string,
  patch: { disabled?: boolean; password?: string },
): Promise<{ ok: boolean }> {
  return api(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
}

export function adminDeleteUser(id: string): Promise<{ ok: boolean }> {
  return api(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/* ---------------- admin: organizations ---------------- */

export function adminGetSettings(): Promise<{ signupEnabled: boolean }> {
  return api('/api/admin/settings');
}

export function adminSetSignup(signupEnabled: boolean): Promise<{ ok: boolean }> {
  return api('/api/admin/settings', { method: 'PATCH', body: { signupEnabled } });
}

export function adminCreateAccount(
  email: string,
  password: string,
): Promise<{ user: { id: string; email: string; role: string } }> {
  return api('/api/admin/users', { body: { email, password } });
}


export function adminListOrgs(): Promise<{ orgs: AdminOrgRow[] }> {
  return api('/api/admin/orgs');
}

export function adminCreateOrg(input: {
  name: string;
  quotaMb: number;
  maxUsers?: number;
  adminUsername: string;
  adminPassword: string;
}): Promise<{ organization: AdminOrgRow }> {
  return api('/api/admin/orgs', { body: input });
}

/** Organization detail with its member accounts. */
export function adminGetOrg(id: string): Promise<{
  organization: { id: string; name: string; quotaMb: number; maxUsers: number; disabled: boolean; createdAt: string };
  members: { id: string; username: string; role: string; disabled: boolean; createdAt: string; projectCount: number; usedBytes: number }[];
}> {
  return api('/api/admin/orgs/' + encodeURIComponent(id));
}

export function adminUpdateOrg(
  id: string,
  patch: { quotaMb?: number; maxUsers?: number; disabled?: boolean },
): Promise<{ ok: boolean }> {
  return api(`/api/admin/orgs/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
}

export function adminDeleteOrg(id: string): Promise<{ ok: boolean }> {
  return api(`/api/admin/orgs/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/* ---------------- organization members (org admin) ---------------- */

export function orgListUsers(): Promise<{ org: OrgInfo; users: OrgMemberRow[] }> {
  return api('/api/org/users');
}

export function orgCreateUser(
  username: string,
  password: string,
  role: 'org_admin' | 'org_manager' | 'member' = 'member',
  orgId?: string,
): Promise<{ user: { id: string; username: string; role: string } }> {
  return api('/api/org/users', { body: { username, password, role, ...(orgId ? { orgId } : {}) } });
}

export function orgUpdateUser(
  id: string,
  patch: { disabled?: boolean; password?: string; role?: 'org_admin' | 'org_manager' | 'member' },
  orgId?: string,
): Promise<{ ok: boolean }> {
  return api(
    `/api/org/users/${encodeURIComponent(id)}${orgId ? `?org=${encodeURIComponent(orgId)}` : ''}`,
    { method: 'PATCH', body: patch },
  );
}

/** Rename the caller's organization (main admin may target any org by id). */
export function orgRename(name: string, orgId?: string): Promise<{ ok: boolean; name: string }> {
  return api('/api/org/rename', { method: 'PATCH', body: { name, ...(orgId ? { orgId } : {}) } });
}

export function orgDeleteUser(id: string): Promise<{ ok: boolean }> {
  return api(`/api/org/users/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
